import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { API_BASE_URL } from '../config/api';

/**
 * SmsConsent — top-level component for BOTH the public form flow and
 * the tokenized recruiter-driven flow.
 *
 * Route mapping (see App.jsx):
 *   /sms-consent           → SmsConsent (no :token param) → public form
 *   /sms-consent/:token    → SmsConsent (with :token)     → tokenized
 *
 * The two flows differ in:
 *   - Data source:  tokenized fetches candidate/recruiter/firm/role
 *                   context from GET /api/sms/consent/:token before
 *                   rendering; public form has no pre-fill.
 *   - Submit URL:   tokenized POSTs to /api/sms/consent/:token/grant;
 *                   public POSTs to /api/sms-opt-ins.
 *   - Fields:       tokenized only asks for phone (name is known);
 *                   public asks for name + phone.
 *
 * They share:
 *   - The overall shell (brand header, card layout, footer)
 *   - Consent + decline handling (Twilio 30923: consent must be
 *     optional, decline must be a supported path)
 *   - Success + decline confirmation screens
 *
 * Kept in one file (per user request) so the two flows can share
 * visual patterns without drifting. Internal split into sub-components
 * so each concern stays legible.
 */
const SmsConsent = () => {
  const { token } = useParams();
  // Presence of a token param determines mode. Router config exposes
  // token only on the /:token route; the plain /sms-consent route
  // renders this same component with token undefined.
  return token ? <TokenizedFlow token={token} /> : <PublicFlow />;
};

// ─── PUBLIC FLOW ────────────────────────────────────────────────────
// ─── PUBLIC FLOW ────────────────────────────────────────────────────
//
// Two-step flow, restructured after TCR review feedback.
//
// Original single-page design had name + phone + consent checkbox all
// rendered on landing, with a "No thanks" secondary button. TCR
// reviewers read this as a forced opt-in even though declining was
// supported — they see form fields and interpret them as required.
//
// New flow:
//   Step 1 ('landing') — no form fields. Two equal-weight buttons:
//     "Yes, opt in to SMS"        → advances to step 2
//     "No thanks — email only"    → records decline, shows done screen
//   Step 2 ('form') — the actual opt-in form. Only reachable after the
//     user has explicitly chosen to opt in. Fields being "required" at
//     this point is honest labeling (they've committed to the path).
//     Back button returns to step 1 without submitting.
//   Done screen — same as before, message varies by outcome.
//
// Behavior when a user closes the browser mid-flow: no data is sent,
// no consent recorded, no phone stored. Fully optional from Twilio's
// perspective — the landing page is a pure intent chooser, no fields.
const PublicFlow = () => {
  const [step, setStep] = useState('landing'); // 'landing' | 'form' | 'done'

  const [phone, setPhone] = useState('');
  const [name, setName] = useState('');
  const [consented, setConsented] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [declined, setDeclined] = useState(false);

  const params = new URLSearchParams(window.location.search);
  const inviteToken = params.get('ref') || params.get('token') || null;

  const CONSENT_TEXT = `I agree to receive recurring SMS text messages from Banyak Group LTD regarding recruitment outreach, job opportunities, interview scheduling, application status updates, and related candidate communications. Message frequency varies (typically 1–4 messages per active conversation). Message and data rates may apply. Reply STOP to any message to opt out, reply HELP for help. By checking this box and submitting this form I confirm I am the authorized user of the phone number provided and I agree to Banyak Group LTD's Terms of Service and Privacy Policy.`;

  const recordConsent = async (consentChoice) => {
    setError('');
    if (consentChoice === 'opt_in') {
      if (!phone.trim()) { setError('Please enter your mobile number.'); return; }
      if (!consented) { setError('Please check the consent box above to opt in. Or use the back button below if you\'ve changed your mind.'); return; }
    }
    setSubmitting(true);
    try {
      const response = await fetch(`${API_BASE_URL}/api/sms-opt-ins`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          phone: phone.trim(),
          name: name.trim(),
          consent_text: consentChoice === 'opt_in' ? CONSENT_TEXT : null,
          consent_url: window.location.href,
          invite_token: inviteToken,
          decision: consentChoice,
        })
      });
      if (!response.ok) {
        const body = await response.json().catch(() => ({}));
        throw new Error(body.error || 'Could not record your choice. Please try again.');
      }
      if (consentChoice === 'decline') setDeclined(true);
      setStep('done');
    } catch (err) {
      setError(err.message || 'Could not record your choice.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleFormSubmit = (e) => { e.preventDefault(); recordConsent('opt_in'); };
  const handleDeclineFromLanding = () => recordConsent('decline');
  const handleBack = () => { setStep('landing'); setError(''); };

  // ─── STEP: DONE ────────────────────────────────────────────────
  if (step === 'done') {
    return (
      <Shell>
        <div className="text-center">
          <svg className={`w-12 h-12 mx-auto mb-4 ${declined ? 'text-gray-400' : 'text-green-600'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          {declined ? (
            <>
              <h1 className="text-xl font-semibold text-gray-900 mb-2">Preference recorded</h1>
              <p className="text-sm text-gray-600 leading-relaxed">
                Thanks. We've recorded that you do not want to receive SMS messages from Banyak Group LTD. Your recruitment process is unaffected — your recruiter will reach you by email or phone instead.
              </p>
              <p className="text-xs text-gray-400 mt-4">You can change your mind anytime by visiting this page again.</p>
            </>
          ) : (
            <>
              <h1 className="text-xl font-semibold text-gray-900 mb-2">Consent recorded</h1>
              <p className="text-sm text-gray-600 leading-relaxed">
                Thanks{name ? `, ${name}` : ''}. We've recorded your consent to receive SMS messages from Banyak Group LTD at {phone}. You can reply <strong>STOP</strong> to any message to opt out at any time, or reply <strong>HELP</strong> for assistance.
              </p>
              <p className="text-xs text-gray-400 mt-4">For questions about this consent record contact oban@banyak.group.</p>
            </>
          )}
        </div>
      </Shell>
    );
  }

  // ─── STEP: LANDING ─────────────────────────────────────────────
  // No form fields rendered. TCR reviewer opens the page and sees
  // an intent chooser, not a data-collection form.
  if (step === 'landing') {
    return (
      <Shell>
        <div className="text-center mb-6">
          <div className="w-14 h-14 mx-auto mb-4 rounded-full bg-blue-50 flex items-center justify-center">
            <svg className="w-7 h-7 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
            </svg>
          </div>
          <h1 className="text-xl font-semibold text-gray-900 mb-2">Would you like SMS updates?</h1>
          <p className="text-sm text-gray-600 leading-relaxed">
            Banyak Group LTD can send you text messages about interview scheduling, application status, and recruiter communications. This is an optional convenience — your recruitment process continues by email and phone either way.
          </p>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 rounded p-3 mb-4"><p className="text-xs text-red-700">{error}</p></div>
        )}

        <div className="space-y-2">
          <button
            onClick={() => setStep('form')}
            disabled={submitting}
            className="w-full px-4 py-3 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed"
          >
            Yes, opt in to SMS
          </button>
          <button
            onClick={handleDeclineFromLanding}
            disabled={submitting}
            className="w-full px-4 py-3 bg-white text-gray-700 text-sm font-medium border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50"
          >
            {submitting ? 'Recording your choice…' : 'No thanks — email only'}
          </button>
        </div>

        <div className="mt-6 pt-4 border-t border-gray-100 text-center">
          <p className="text-[11px] text-gray-500 leading-relaxed">
            You may also close this page without choosing. Doing so records no phone number and no consent — the recruitment process continues by email regardless.
          </p>
        </div>
      </Shell>
    );
  }

  // ─── STEP: FORM ────────────────────────────────────────────────
  // Only reachable after user clicked "Yes, opt in" on landing.
  // The user has committed to this path; required labeling here is
  // honest, not a forced opt-in.
  return (
    <Shell>
      <div className="flex items-center gap-2 mb-4">
        <button
          onClick={handleBack}
          className="p-1 text-gray-500 hover:text-gray-900 hover:bg-gray-100 rounded flex items-center gap-1"
          type="button"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          <span className="text-xs">Back</span>
        </button>
      </div>
      <h2 className="text-lg font-semibold text-gray-900 mb-3">Opt in to receive SMS messages</h2>
      <p className="text-sm text-gray-600 leading-relaxed mb-6">
        Enter your details below to complete opt-in. You can go back at any time to keep email-only communication.
      </p>

      <form onSubmit={handleFormSubmit} className="space-y-4">
        <div>
          <label htmlFor="sms-name" className="block text-xs font-medium text-gray-700 mb-1">Full name</label>
          <input id="sms-name" type="text" value={name} onChange={(e) => setName(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            autoComplete="name" />
        </div>
        <div>
          <label htmlFor="sms-phone" className="block text-xs font-medium text-gray-700 mb-1">Mobile phone number</label>
          <input id="sms-phone" type="tel" placeholder="+1 (555) 123-4567"
            value={phone} onChange={(e) => setPhone(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            autoComplete="tel" />
        </div>

        <label htmlFor="sms-consent" className="flex items-start gap-3 cursor-pointer">
          <input id="sms-consent" type="checkbox" checked={consented}
            onChange={(e) => setConsented(e.target.checked)}
            className="mt-1 h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500" />
          <span className="text-xs text-gray-700 leading-relaxed">
            I agree to receive <strong>recurring SMS text messages</strong> from <strong>Banyak Group LTD</strong> regarding recruitment outreach, job opportunities, interview scheduling, application status updates, and related candidate communications. <strong>Message frequency varies</strong> (typically 1–4 messages per active conversation). <strong>Message and data rates may apply.</strong> Reply <strong>STOP</strong> to any message to opt out, reply <strong>HELP</strong> for help. By checking this box and submitting this form I confirm I am the authorized user of the phone number provided and I agree to Banyak Group LTD's{' '}
            <a href="/terms" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">Terms of Service</a> and{' '}
            <a href="/privacy" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">Privacy Policy</a>.
          </span>
        </label>

        {error && (
          <div className="bg-red-50 border border-red-200 rounded p-3"><p className="text-xs text-red-700">{error}</p></div>
        )}

        <div className="space-y-2">
          <button type="submit" disabled={submitting}
            className="w-full px-4 py-2.5 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed">
            {submitting ? 'Recording your consent…' : 'Complete opt-in'}
          </button>
          <button type="button" onClick={handleBack} disabled={submitting}
            className="w-full px-4 py-2 bg-white text-gray-600 text-sm font-medium border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50">
            Back
          </button>
        </div>
      </form>
    </Shell>
  );
};

// ─── TOKENIZED FLOW ─────────────────────────────────────────────────
const TokenizedFlow = ({ token }) => {
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [data, setData] = useState(null);
  const [phone, setPhone] = useState('');
  const [consented, setConsented] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [outcome, setOutcome] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${API_BASE_URL}/api/sms/consent/${token}`);
        if (!res.ok) {
          if (cancelled) return;
          setLoadError(res.status === 404 ? 'This consent link is invalid or expired.' : "We couldn't load your consent page.");
          setLoading(false);
          return;
        }
        const body = await res.json();
        if (cancelled) return;
        setData(body);
        setLoading(false);
      } catch (e) {
        if (cancelled) return;
        setLoadError('Network error loading the consent page.');
        setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [token]);

  const handleGrant = async (e) => {
    e.preventDefault();
    setSubmitError('');
    if (!phone.trim()) { setSubmitError('Please enter your mobile number.'); return; }
    if (!consented) { setSubmitError('Please check the consent box above to opt in. Or click "No thanks" below if you prefer not to receive SMS.'); return; }
    setSubmitting(true);
    try {
      const res = await fetch(`${API_BASE_URL}/api/sms/consent/${token}/grant`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ phone_number: phone.trim() }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.error || 'Could not record your consent.');
      }
      setOutcome('granted');
    } catch (err) {
      setSubmitError(err.message || 'Could not record your consent.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDecline = () => setOutcome('declined');

  if (loading) {
    return <Shell><div className="text-sm text-gray-500 text-center py-4">Loading…</div></Shell>;
  }
  if (loadError) {
    return (
      <Shell>
        <div className="text-center">
          <h1 className="text-xl font-semibold text-gray-900 mb-2">Consent link not available</h1>
          <p className="text-sm text-gray-600">{loadError}</p>
          <p className="text-xs text-gray-500 mt-4">
            You can still opt in from our generic consent page:{' '}
            <a href="/sms-consent" className="text-blue-600 hover:underline">app.banyak.group/sms-consent</a>
          </p>
        </div>
      </Shell>
    );
  }
  if (data?.status === 'granted' && !outcome) {
    return (
      <Shell>
        <div className="text-center">
          <svg className="w-12 h-12 mx-auto mb-4 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <h1 className="text-xl font-semibold text-gray-900 mb-2">You're already opted in</h1>
          <p className="text-sm text-gray-600 leading-relaxed">
            Consent for SMS messages from {data.firm_name || 'Banyak Group'} was recorded
            {data.granted_at ? ` on ${new Date(data.granted_at).toLocaleDateString()}` : ''}
            {data.phone_number_last4 ? ` for the number ending in ${data.phone_number_last4}` : ''}. Reply <strong>STOP</strong> to any message to opt out.
          </p>
        </div>
      </Shell>
    );
  }
  if (data?.status === 'revoked') {
    return (
      <Shell>
        <div className="text-center">
          <h1 className="text-xl font-semibold text-gray-900 mb-2">This consent link is no longer active</h1>
          <p className="text-sm text-gray-600">
            Please contact {data.recruiter_name ? `${data.recruiter_name} at ${data.recruiter_email || 'Banyak Group'}` : 'your Banyak Group recruiter'} for a new link.
          </p>
        </div>
      </Shell>
    );
  }
  if (outcome === 'granted') {
    return (
      <Shell>
        <div className="text-center">
          <svg className="w-12 h-12 mx-auto mb-4 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <h1 className="text-xl font-semibold text-gray-900 mb-2">Consent recorded</h1>
          <p className="text-sm text-gray-600 leading-relaxed">
            Thanks{data?.candidate_name ? `, ${data.candidate_name.split(' ')[0]}` : ''}. We've recorded your consent to receive SMS from {data?.firm_name || 'Banyak Group'} at {phone}. Reply <strong>STOP</strong> to any message to opt out, or <strong>HELP</strong> for assistance.
          </p>
        </div>
      </Shell>
    );
  }
  if (outcome === 'declined') {
    return (
      <Shell>
        <div className="text-center">
          <svg className="w-12 h-12 mx-auto mb-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <h1 className="text-xl font-semibold text-gray-900 mb-2">No problem</h1>
          <p className="text-sm text-gray-600 leading-relaxed">
            Your recruitment process isn't affected — {data?.recruiter_name || 'your recruiter'} will reach you by email instead. You can revisit this link anytime if you change your mind.
          </p>
        </div>
      </Shell>
    );
  }

  const firmName = data?.firm_name || 'Banyak Group';
  const roleContext = data?.role_context;
  const recruiter = data?.recruiter_name;

  return (
    <Shell>
      <h2 className="text-lg font-semibold text-gray-900 mb-3">
        Hi{data?.candidate_name ? ` ${data.candidate_name.split(' ')[0]}` : ''} — quick opt-in for SMS
      </h2>
      <p className="text-sm text-gray-600 leading-relaxed mb-3">
        {recruiter ? `${recruiter} at ${firmName}` : firmName} would like to send you SMS text messages{roleContext ? ` about the ${roleContext} opportunity` : ''} and related recruitment updates. This is optional — declining is completely fine and won't affect your recruitment process.
      </p>
      <div className="bg-blue-50 border border-blue-200 rounded-md p-3 mb-6 text-xs text-blue-900 leading-relaxed">
        <strong>SMS messaging is optional.</strong> Consent is not required to work with a {firmName} recruiter or to be considered for any role. If you prefer email, use the "No thanks" button below.
      </div>

      <form onSubmit={handleGrant} className="space-y-4">
        <div>
          <label htmlFor="sms-phone" className="block text-xs font-medium text-gray-700 mb-1">
            Mobile phone number <span className="text-gray-400">(required for opt-in)</span>
          </label>
          <input id="sms-phone" type="tel" placeholder="+1 (555) 123-4567"
            value={phone} onChange={(e) => setPhone(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            autoComplete="tel" />
        </div>

        <label htmlFor="sms-consent" className="flex items-start gap-3 cursor-pointer">
          <input id="sms-consent" type="checkbox" checked={consented}
            onChange={(e) => setConsented(e.target.checked)}
            className="mt-1 h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500" />
          <span className="text-xs text-gray-700 leading-relaxed">
            <span className="text-gray-400 font-medium">(optional)</span>{' '}
            {data?.consent_text || `I agree to receive recurring SMS text messages from ${firmName} regarding recruitment outreach, interview scheduling, application status updates, and related career discussions. Message frequency varies (typically 1-4 messages per active conversation). Message and data rates may apply. Reply STOP to opt out, or HELP for assistance. I confirm I am the authorized user of the phone number provided.`}
          </span>
        </label>

        {submitError && (
          <div className="bg-red-50 border border-red-200 rounded p-3"><p className="text-xs text-red-700">{submitError}</p></div>
        )}

        <div className="space-y-2">
          <button type="submit" disabled={submitting}
            className="w-full px-4 py-2.5 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed">
            {submitting ? 'Recording your consent…' : 'Opt in to SMS'}
          </button>
          <button type="button" onClick={handleDecline} disabled={submitting}
            className="w-full px-4 py-2 bg-white text-gray-600 text-sm font-medium border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50">
            No thanks — I prefer email
          </button>
        </div>
      </form>

      <div className="mt-6 text-center space-y-1">
        <p className="text-[11px] text-gray-500">
          {firmName} will not share your mobile information with third parties or affiliates for marketing purposes.
        </p>
        <p className="text-[11px] text-gray-400">
          Questions? Contact {data?.recruiter_email || 'oban@banyak.group'} · <a href="/privacy" className="text-blue-600 hover:underline">Privacy Policy</a> · <a href="/terms" className="text-blue-600 hover:underline">Terms</a>
        </p>
      </div>
    </Shell>
  );
};

// ─── Shared shell ─────────────────────────────────────
const Shell = ({ children }) => (
  <div className="min-h-screen bg-gray-50 px-4 py-10">
    <div className="max-w-xl mx-auto">
      <div className="text-center mb-6">
        <h1 className="text-2xl font-semibold text-gray-900">Banyak Group LTD</h1>
        <p className="text-sm text-gray-500 mt-1">SMS Messaging Consent</p>
      </div>
      <div className="bg-white rounded-lg shadow p-6 sm:p-8">
        {children}
      </div>
      <div className="mt-6 text-center">
        <p className="text-[11px] text-gray-400">
          <a href="/privacy" className="text-blue-600 hover:underline">Privacy Policy</a> · <a href="/terms" className="text-blue-600 hover:underline">Terms</a>
        </p>
      </div>
    </div>
  </div>
);

export default SmsConsent;
